import React, { useRef, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { notify } from '../../../utils/notification';
import { decideDefenderReaction } from '../utils/combatSaveStateService';
import { reactionSlotOptions } from '../utils/reactionSlotOptions';
import { HELLISH_REBUKE_FEATURE_ID, hellishRebukeDiceCount } from '../../../shared/rules/effects/reactionSpells.mjs';

const FREE = 'free';
/** One line for the result: the save, then the damage. */
export const describeRebuke = ({ targetName, damage, save }) =>
  `Hellish Rebuke: ${targetName} rolled ${save.total} against DC ${save.dc} and ${save.success ? 'saved' : 'failed'}, taking ${damage} Fire damage.`;

/**
 * Hellish Rebuke after a creature within 60 feet damages you: the Reaction and a slot, or an Infernal Tiefling's free cast. The
 * d10s roll here and stay with the offer, so a retry sends the same dice.
 */
export default function HellishRebukePrompt({ campaignId, characterId, form, combatState, onCombatChange, rollDice, onResolved }) {
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState('');
  const [canSee, setCanSee] = useState(false);
  const lock = useRef(false), rolled = useRef({});
  const offer = (combatState?.reactionOpportunities || []).find((o) => o.status === 'pending' && o.trigger === 'hellish-rebuke' && String(o.targetCombatantId) === String(characterId)
    && o.createdAtTurnSequence === combatState?.turnSequence);
  const slots = offer?.slotCast ? reactionSlotOptions(form) : [];
  const options = [...(offer?.freeCast ? [{ value: FREE, label: 'Free (Fiendish Legacy, once per Long Rest)' }] : []),
    ...slots.map((option, index) => ({ value: String(index), label: option.label }))];
  const selected = options.find((option) => option.value === choice) || options[0];
  const slot = selected?.value === FREE ? null : slots[Number(selected?.value)];
  const decide = async (decision) => {
    if (lock.current || !offer) return;
    lock.current = true; setBusy(true);
    try {
      let extra = {};
      if (decision === 'use') {
        const count = hellishRebukeDiceCount(slot ? slot.slotLevel : 1), key = `${offer.opportunityId}:${count}`;
        rolled.current[key] ||= await rollDice(count);
        extra = { canSee, dice: rolled.current[key], ...(slot ? { mode: 'slot', pool: slot.pool, slotLevel: slot.slotLevel } : { mode: FREE }) };
      }
      const result = await decideDefenderReaction(campaignId, offer.opportunityId, decision, HELLISH_REBUKE_FEATURE_ID, extra);
      onCombatChange?.(result.combat);
      setChoice(''); setCanSee(false);
      if (result.rebuke) { notify(describeRebuke(result.rebuke), 'success'); onResolved?.(result.rebuke); }
    } catch (error) { notify(error.message || 'Hellish Rebuke could not be cast.', 'danger'); }
    finally { lock.current = false; setBusy(false); }
  };
  return (
    <Modal show={Boolean(offer)} centered backdrop="static" keyboard={false} className="dnd-modal modern-modal" aria-label="Hellish Rebuke">
      <Modal.Header><Modal.Title>Hellish Rebuke</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>{offer?.sourceName} damaged you. Use your Reaction to make it attempt a Dexterity save, taking Fire damage on a failure and half on a success?</p>
        {options.length > 1 && <Form.Select className="mb-2" aria-label="Cast with" value={selected?.value} disabled={busy} onChange={(event) => setChoice(event.target.value)}>
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </Form.Select>}
        <Form.Check id="hellish-rebuke-can-see" type="checkbox" label={`I can see ${offer?.sourceName || 'this creature'}`} checked={canSee} disabled={busy} onChange={(event) => setCanSee(event.target.checked)} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={() => decide('skip')}>Skip</Button>
        <Button disabled={busy || !selected || !canSee} onClick={() => decide('use')}>{busy ? 'Casting…' : 'Cast Hellish Rebuke'}</Button>
      </Modal.Footer>
    </Modal>
  );
}
